import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Typography,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { Formik, Form } from 'formik';

import { IPolicy } from 'interfaces/Policy';
import FormNavigationButtons from 'components/FormNavigationButtons';
import { REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { RootState } from 'store';
import {
  createPolicyAction,
  updatePolicyAction,
  setIsCreating,
} from 'modules/policy';

import { IPolicyRegisterProps } from './interfaces';
import PolicyRegisterForm from './PolicyRegisterForm';
import { createPolicyValidationSchema } from './validation';
import { useStyles } from './styles';

const PolicyRegister: React.FC<IPolicyRegisterProps> = ({
  policy,
}: IPolicyRegisterProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { loading } = useSelector((state: RootState) => state.policy);
  const [isViewOnly, setIsViewOnly] = useState(policy._id !== 'new');
  const [expanded, setExpanded] = useState(policy._id === 'new');

  const { permissions } = useSelector((state: RootState) => state.user);

  const handleSubmit = (values: IPolicy) => {
    if (policy._id === 'new') {
      dispatch(createPolicyAction(values));
    } else {
      dispatch(updatePolicyAction({ id: policy._id, policy: values }));
    }
  };

  const handleChange = (
    event: React.ChangeEvent<unknown>,
    isExpanded: boolean,
  ) => {
    setExpanded(isExpanded);
    if (!isExpanded) {
      setIsViewOnly(true);
    }
  };

  const handleEdit = (isEditing: boolean, resetForm?: () => void) => (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    setIsViewOnly(!isEditing);
    if (isEditing) {
      setExpanded(true);
    } else {
      resetForm && resetForm();
      if (policy._id === 'new') {
        dispatch(setIsCreating(false));
      }
    }
  };

  return (
    <Accordion
      data-cy="policy-register__container--accordion"
      expanded={expanded}
      onChange={isViewOnly ? handleChange : undefined}
    >
      <AccordionSummary data-cy="policy-register__accordion--summary">
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="stretch"
        >
          <Grid item>
            <Typography data-cy="policy-register__label--description">
              {isViewOnly
                ? policy.name
                : policy._id === 'new'
                ? 'Novo perfil'
                : 'Edição de perfil'}
            </Typography>
          </Grid>
          <Grid item>
            {isViewOnly && permissions['policy:update'] && (
              <IconButton
                data-cy="policy-register__button--edit"
                size="small"
                onClick={handleEdit(true)}
              >
                <Edit color="primary" />
              </IconButton>
            )}
          </Grid>
        </Grid>
      </AccordionSummary>
      {expanded && (
        <AccordionDetails data-cy="policy-register__accordion--details">
          <Formik
            initialValues={policy}
            onSubmit={handleSubmit}
            validationSchema={createPolicyValidationSchema}
          >
            {({ resetForm }) => (
              <Form className={classes.form}>
                <PolicyRegisterForm isViewOnly={isViewOnly} />
                {!isViewOnly && (
                  <FormNavigationButtons
                    isViewOnly={isViewOnly}
                    backButtonOnClick={handleEdit(false, resetForm)}
                    submitButtonLabel="Salvar"
                    backButtonLabel="Cancelar"
                    isSubmitting={loading === REQUEST_LIFE_CICLE.PENDING}
                  />
                )}
              </Form>
            )}
          </Formik>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default PolicyRegister;
